
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Menu, PanelLeftClose, X } from 'lucide-react';
import { SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { LocationSwitcher } from "./LocationSwitcher";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { cn } from "@/lib/utils";

interface HeaderProps {
  title: string;
  showBackButton?: boolean;
  rightAction?: React.ReactNode;
}

const Header: React.FC<HeaderProps> = ({ title, showBackButton = false, rightAction }) => {
  const navigate = useNavigate();
  const { isMobile, openMobile, open } = useSidebar();
  const menuOpen = isMobile ? openMobile : open;

  return (
    <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
      <div className="w-full max-w-screen-xl mx-auto px-3 sm:px-4 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <SidebarTrigger
              size="default"
              aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              title={menuOpen ? 'Close menu' : 'Open menu'}
              className={cn(
                'h-11 shrink-0 gap-1.5 rounded-xl border-2 px-3 shadow-sm',
                'hover:bg-teal-50 dark:hover:bg-teal-950/40',
                'border-teal-600/70 bg-teal-50 text-teal-800',
                'dark:border-teal-500/60 dark:bg-teal-950/50 dark:text-teal-100',
                'focus-visible:ring-2 focus-visible:ring-teal-500',
                menuOpen && 'border-teal-700 bg-teal-100 dark:bg-teal-900/70',
              )}
            >
              {menuOpen ? (
                isMobile ? <X className="h-5 w-5" strokeWidth={2.5} /> : <PanelLeftClose className="h-5 w-5" strokeWidth={2.25} />
              ) : (
                <Menu className="h-5 w-5" strokeWidth={2.5} />
              )}
              <span className="text-sm font-semibold pr-0.5 sm:inline">
                {menuOpen ? 'Close' : 'Menu'}
              </span>
            </SidebarTrigger>
            {showBackButton && (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="p-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl"
                aria-label="Go back"
              >
                <ArrowLeft size={20} className="text-gray-600 dark:text-gray-300" />
              </button>
            )}
            <h1 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
              {title}
            </h1>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <LocationSwitcher />
            <LanguageSwitcher />
            <NotificationBell />
            {rightAction}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
